import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    _linkedin_partner_id: string;
    _linkedin_data_partner_ids: string[];
    lintrk: any;
  }
}

interface LinkedInTrackingProps {
  partnerId: string;
}

export const LinkedInTracking = ({ partnerId }: LinkedInTrackingProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Guard against SSR - only run in browser
    if (!isMounted || typeof window === "undefined") return;

    // Defer LinkedIn Insight Tag until browser is idle
    const loadLinkedIn = () => {
      window._linkedin_partner_id = partnerId;
      window._linkedin_data_partner_ids =
        window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(partnerId);

      if (!window.lintrk) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        document.body.appendChild(script);
      }
    };

    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(loadLinkedIn, { timeout: 4000 });
    } else {
      setTimeout(loadLinkedIn, 3000);
    }
  }, [isMounted, partnerId]);

  // Track page views on route change
  useEffect(() => {
    if (!isMounted || typeof window === "undefined") return;

    if (window.lintrk) {
      try {
        window.lintrk("track", { conversion_id: "pageview" });
      } catch (error) {
        console.error("LinkedIn tracking error:", error);
      }
    }
  }, [isMounted, location.pathname]);

  return null;
};

// Helper function to track custom conversions
export const trackLinkedInConversion = (
  conversionId: string,
  value?: number,
  currency?: string,
) => {
  if (typeof window === "undefined") return;

  if (window.lintrk) {
    try {
      const eventData: any = { conversion_id: conversionId };
      if (value) eventData.value = value;
      if (currency) eventData.currency = currency;

      window.lintrk("track", eventData);
      console.log("LinkedIn conversion tracked:", conversionId);
    } catch (error) {
      console.error("LinkedIn conversion tracking error:", error);
    }
  } else {
    console.warn("LinkedIn Insight Tag not loaded yet");
  }
};
